import { type KeyboardEvent, useRef, useState } from 'react';
import clsx from 'clsx';
import type { Word } from '@/types';

interface Props {
  wordData: Word;
  onCorrect: () => void;
  locked: boolean;
}

export const WordInput = ({ wordData, onCorrect, locked }: Props) => {
  const [letters, setLetters] = useState<string[]>(Array(wordData.word.length).fill(''));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const handleLetterChange = (index: number, value: string) => {
    if (locked) return;

    const newLetters = [...letters];
    newLetters[index] = value;
    setLetters(newLetters);

    // Autofocus next input
    if (value && index < wordData.word.length - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    // Check if word is correct (case-insensitive)
    const enteredWord = newLetters.join('');
    if (enteredWord.toUpperCase() === wordData.word.toUpperCase()) {
      onCorrect();
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    // Move to previous input on backspace if current is empty
    if (e.key === 'Backspace' && !letters[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  return (
    <div className="flex items-center gap-2 md:gap-4">
      <div
        className={clsx(
          'size-8 md:size-12 rounded-full border-2 border-current flex items-center justify-center md:text-xl shrink-0',
          locked ? 'border-solid' : 'border-dashed',
        )}
      >
        {wordData.id}
      </div>

      <div className="grid grid-cols-8 gap-1">
        {Array.from({ length: wordData.word.length }).map((_, index) => (
          <input
            key={index}
            ref={el => {
              inputRefs.current[index] = el;
            }}
            type="text"
            maxLength={1}
            value={letters[index]}
            onChange={e => handleLetterChange(index, e.target.value.toUpperCase())}
            onKeyDown={e => handleKeyDown(index, e)}
            disabled={locked}
            className={clsx(
              'md:w-12 md:h-16 text-center text-lg md:text-2xl font-bold uppercase bg-transparent border-current border-2 border-dashed',
              'disabled:border-solid disabled:cursor-not-allowed',
              'focus:outline-none focus:border-black',
            )}
          />
        ))}
      </div>
    </div>
  );
};
